import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowDownLeft, ArrowUpRight, Loader2 } from 'lucide-react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { NumberInput } from '@/components/ui/number-input'
import { FormSelect } from '@/components/ui/select'
import { isApiError } from '@/lib/api/errors'
import { cn } from '@/lib/utils'
import { useSaveCashEntry } from '../api'
import type { AccountType } from '../types'
import { currencySuffix, fmtAmount } from '../fx'
import { toastError, useFinanceLookups } from '../ui'
import { Field, FormSection } from './FormBits'
import { useFinanceDepartments } from '../departments'

type Props = { direction: 'in' | 'out'; accountId?: string; accountTypes?: readonly AccountType[]; title?: string; onDone: () => void }

/** Faturaya bağlı olmayan kasa/hesap girişi ya da çıkışı. */
export function CashEntryForm({ direction, accountId, accountTypes, title, onDone }: Props) {
  const { t } = useTranslation()
  const save = useSaveCashEntry()
  const lookups = useFinanceLookups()
  const { categories } = lookups
  const departments = useFinanceDepartments()
  const accounts = accountTypes ? lookups.accounts.filter((a) => accountTypes.includes(a.type)) : lookups.accounts

  const schema = z.object({
    direction: z.enum(['in', 'out']),
    accountId: z.string().min(1, t('finance.accounts.form.accountRequired')),
    date: z.string().min(1, t('finance.form.dateRequired')),
    amount: z.string().regex(/^\d+(\.\d{1,2})?$/, t('finance.form.amountInvalid')).refine((v) => Number(v) > 0, t('finance.form.amountInvalid')),
    categoryId: z.string(),
    description: z.string().trim().min(2, t('finance.entry.descriptionRequired')).max(160),
    reference: z.string().trim().max(60),
    fxRate: z.string().trim().regex(/^(\d+([.,]\d{1,6})?)?$/, t('finance.fx.rateInvalid')),
    departmentId: z.string(),
  })
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      direction,
      accountId: accountId ?? (accounts.find((a) => a.type === 'cash' && a.active) ?? accounts.find((a) => a.active))?.id ?? '',
      date: new Date().toISOString().slice(0, 10),
      amount: '',
      categoryId: '',
      description: '',
      reference: '',
      fxRate: '',
      departmentId: '',
    },
  })
  const { errors } = form.formState
  const dir = useWatch({ control: form.control, name: 'direction' })
  const kind = dir === 'in' ? 'income' : 'expense'
  const selectedId = useWatch({ control: form.control, name: 'accountId' })
  const selected = accounts.find((a) => a.id === selectedId)
  const currency = selected?.currency || 'TRY'

  const onSubmit = form.handleSubmit(async (v) => {
    try {
      const res = await save.mutateAsync({ ...v, categoryId: v.categoryId || null, fxRate: currency !== 'TRY' ? v.fxRate : undefined })
      if (res.pendingApproval)
        toast.info(t('finance.paymentRequests.sent'), { description: t('finance.paymentRequests.sentHint') })
      else toast.success(t(v.direction === 'in' ? 'finance.entry.savedIn' : 'finance.entry.savedOut'))
      onDone()
    } catch (e) {
      if (isApiError(e) && e.code === 'INSUFFICIENT_BALANCE') form.setError('amount', { message: t('errors.INSUFFICIENT_BALANCE') })
      else toastError(e)
    }
  })

  return (
    <Modal
      open
      size="lg"
      onOpenChange={(v) => !v && onDone()}
      dismissible={!save.isPending}
      icon={dir === 'in' ? <ArrowDownLeft /> : <ArrowUpRight />}
      title={(dir === direction && title) || t(dir === 'in' ? 'finance.entry.titleIn' : 'finance.entry.titleOut')}
      description={t('finance.entry.hint')}
      onSubmit={(e) => void onSubmit(e)}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <Controller
          control={form.control}
          name="direction"
          render={({ field }) => (
            <div className="grid grid-cols-2 rounded-lg border bg-muted/40 p-1" role="radiogroup" aria-label={t('finance.entry.direction')}>
              {(['in', 'out'] as const).map((d) => (
                <button
                  key={d}
                  type="button"
                  role="radio"
                  aria-checked={field.value === d}
                  onClick={() => {
                    field.onChange(d)
                    form.setValue('categoryId', '')
                  }}
                  className={cn(
                    'inline-flex items-center justify-center gap-1.5 rounded-md py-1.5 text-sm font-medium transition-colors',
                    field.value === d ? (d === 'in' ? 'bg-success text-white shadow-sm' : 'bg-destructive text-destructive-foreground shadow-sm') : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {d === 'in' ? <ArrowDownLeft className="size-4" aria-hidden /> : <ArrowUpRight className="size-4" aria-hidden />}
                  {t(`finance.entry.${d}`)}
                </button>
              ))}
            </div>
          )}
        />
        <FormSection>
          <Field id="ce-account" label={t('finance.pay.account')} error={errors.accountId?.message}>
            <FormSelect
              id="ce-account"
              control={form.control}
              name="accountId"
              placeholder={t('common.select')}
              options={accounts.filter((a) => a.active).map((a) => ({ value: a.id, label: a.name, description: `${t(`finance.accountType.${a.type}`)} · ${fmtAmount(a.balance, a.currency)}` }))}
            />
          </Field>
          <Field id="ce-date" label={t('finance.entry.date')} error={errors.date?.message}>
            <Controller control={form.control} name="date" render={({ field }) => <DateField id="ce-date" value={field.value} onChange={field.onChange} clearable={false} />} />
          </Field>
          <Field id="ce-amount" label={t('finance.amount')} error={errors.amount?.message}>
            <Controller control={form.control} name="amount" render={({ field }) => <NumberInput id="ce-amount" value={field.value} onChange={field.onChange} onBlur={field.onBlur} minDecimalsOnBlur={2} suffix={currencySuffix(currency)} aria-invalid={!!errors.amount} />} />
          </Field>
          {currency !== 'TRY' ? (
            <Field id="ce-fx" label={t('finance.fx.bankRate', { currency })} error={errors.fxRate?.message} hint={t('finance.fx.bankRateHint', { rate: selected?.fxRate || '—' })}>
              <Input id="ce-fx" inputMode="decimal" placeholder={selected?.fxRate} {...form.register('fxRate')} />
            </Field>
          ) : null}
          <Field id="ce-cat" label={t('finance.category')} hint={t('finance.entry.categoryHint')}>
            <FormSelect
              id="ce-cat"
              control={form.control}
              name="categoryId"
              options={[{ value: '', label: t(dir === 'in' ? 'finance.entry.noCategoryIn' : 'finance.entry.noCategoryOut') }, ...categories.filter((c) => c.kind === kind && c.active).map((c) => ({ value: c.id, label: c.name }))]}
            />
          </Field>
          {dir === 'out' && departments.data?.length ? (
            <Field id="ce-dept" label={t('finance.departments.field')} hint={t('finance.departments.fieldHint')}>
              <FormSelect
                id="ce-dept"
                control={form.control}
                name="departmentId"
                options={[{ value: '', label: t('finance.departments.categoryDefault') }, ...departments.data.map((d) => ({ value: d.id, label: d.name }))]}
              />
            </Field>
          ) : null}
          <Field id="ce-desc" label={t('finance.tx.description')} error={errors.description?.message} full>
            <Input id="ce-desc" maxLength={160} placeholder={t(dir === 'in' ? 'finance.entry.placeholderIn' : 'finance.entry.placeholderOut')} aria-invalid={!!errors.description} {...form.register('description')} />
          </Field>
          <Field id="ce-ref" label={t('finance.entry.reference')} full>
            <Input id="ce-ref" maxLength={60} {...form.register('reference')} />
          </Field>
        </FormSection>
      </div>
    </Modal>
  )
}
